import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  Users, ClockClockwise, CheckCircle, Warning, Phone, WhatsappLogo,
  Receipt, Flame, Trophy, XCircle, FileText, CurrencyInr, Target,
} from "@phosphor-icons/react";

const KPI_CONFIG = [
  { key: "todays_assigned_leads",      label: "Today's New Leads",         icon: Users,            tone: "blue" },
  { key: "todays_pending_followups",   label: "Pending Follow-ups Today",  icon: ClockClockwise,   tone: "amber" },
  { key: "todays_completed_followups", label: "Completed Today",           icon: CheckCircle,      tone: "emerald" },
  { key: "missed_followups",           label: "Missed Follow-ups",         icon: Warning,          tone: "rose" },
  { key: "calls_done",                 label: "Calls Done Today",          icon: Phone,            tone: "blue" },
  { key: "whatsapp_sent",              label: "WhatsApp Sent Today",       icon: WhatsappLogo,     tone: "emerald" },
  { key: "quotations_sent",            label: "Quotations Sent Today",     icon: Receipt,          tone: "indigo" },
  { key: "hot_leads",                  label: "Hot Leads",                 icon: Flame,            tone: "rose" },
  { key: "converted_leads",            label: "Converted Leads",           icon: Trophy,           tone: "emerald" },
  { key: "lost_leads",                 label: "Lost Leads",                icon: XCircle,          tone: "slate" },
  { key: "pending_quotation_followups",label: "Pending Quotation F/Ups",   icon: FileText,         tone: "amber" },
  { key: "pending_payment_followups",  label: "Pending Payment F/Ups",     icon: CurrencyInr,      tone: "amber" },
];

const TONE_CLASSES = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200" },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   border: "border-slate-200" },
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const BDEDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.user_id && !user?.name) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (user.user_id) params.set("user_id", user.user_id);
      if (user.name) params.set("user_name", user.name);
      const res = await axios.get(`${API}/dashboard/bde?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load BDE dashboard");
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, user?.name]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">My Day</div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                BDE Dashboard
              </h1>
              <p className="text-base leading-relaxed text-slate-700">
                {user?.name} — {data?.total_my_leads ?? 0} leads assigned to you in total
              </p>
            </div>
            <button
              data-testid="bde-refresh-btn"
              onClick={load}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium transition-colors"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : !data ? (
            <div className="text-slate-500">No data available.</div>
          ) : (
            <>
              <div data-testid="bde-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI_CONFIG.map(k => {
                  const Icon = k.icon;
                  const tone = TONE_CLASSES[k.tone] || TONE_CLASSES.slate;
                  return (
                    <div
                      key={k.key}
                      data-testid={`bde-kpi-${k.key}`}
                      className={`bg-white border ${tone.border} p-5 rounded-none shadow-sm hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-9 h-9 ${tone.bg} flex items-center justify-center`}>
                          <Icon size={18} weight="bold" className={tone.text} />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-slate-900 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                        {data[k.key] ?? 0}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1.5">
                        {k.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-white border border-slate-200 rounded-none shadow-sm">
                <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
                  <Target size={16} weight="bold" className="text-[#002FA7]" />
                  <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Upcoming Follow-ups</h3>
                </div>
                {(!data.upcoming_followups || data.upcoming_followups.length === 0) ? (
                  <div data-testid="bde-upcoming-empty" className="p-10 text-center text-slate-400 text-sm">
                    No follow-ups scheduled yet. Open a lead and log one to plan your day.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Lead</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Type</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Outcome</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Scheduled</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.upcoming_followups.map((u, i) => (
                        <tr key={u.follow_up_id} data-testid={`bde-upcoming-row-${i}`} className="border-b border-slate-100">
                          <td className="p-3">
                            <div className="font-medium text-slate-900">{u.lead_name || "—"}</div>
                            {u.lead_company && <div className="text-xs text-slate-500">{u.lead_company}</div>}
                          </td>
                          <td className="p-3 text-xs uppercase tracking-wider text-[#002FA7] font-semibold">{(u.type || "").replace(/_/g, " ")}</td>
                          <td className="p-3 text-xs uppercase tracking-wider text-slate-600">{u.outcome}</td>
                          <td className="p-3 text-sm text-slate-700">{formatDateTime(u.next_follow_up_at)}</td>
                          <td className="p-3 text-sm text-slate-600 max-w-md truncate">{u.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BDEDashboard;
